<script setup lang="ts">
import { computed } from 'vue';
import PlsButton1 from '@/components/PlsButton1.vue';
import {
  useCsatSkipButtonCustomisation,
  useCsatSubmitButtonCustomisation,
} from '../composables/useCsatButtonsCustomisation';
import type { RatingValue } from './RatingEmoji.types';

interface Props {
  selectedRating: RatingValue | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'submit'): void;
  (event: 'skip'): void;
}>();

const {
  buttonText: skipButtonTextValue,
  buttonBackgroundColor: skipButtonBackgroundColor,
  buttonTextColor: skipButtonTextColor,
  buttonBorderColor: skipButtonBorderColor,
  isVisible: isSkipButtonVisible,
} = useCsatSkipButtonCustomisation();

const {
  buttonText: submitButtonTextValue,
  buttonBackgroundColor: submitButtonBackgroundColor,
  buttonTextColor: submitButtonTextColor,
  buttonBorderColor: submitButtonBorderColor,
  isVisible: isSubmitButtonVisible,
} = useCsatSubmitButtonCustomisation();

const canSubmit = computed(() => {
  return props.selectedRating !== null;
});

const handleSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit');
};

const handleSkip = () => {
  emit('skip');
};
</script>

<template>
  <div class="csat-actions">
    <PlsButton1
      v-if="isSkipButtonVisible"
      :text="skipButtonTextValue"
      :border-color="skipButtonBorderColor"
      :background-color="skipButtonBackgroundColor"
      :text-color="skipButtonTextColor"
      data-testid="csat-skip"
      @click="handleSkip"
    />
    <PlsButton1
      v-if="isSubmitButtonVisible"
      :text="submitButtonTextValue"
      :border-color="submitButtonBorderColor"
      :background-color="submitButtonBackgroundColor"
      :text-color="submitButtonTextColor"
      :disabled="!canSubmit"
      data-testid="csat-submit"
      @click="handleSubmit"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/mixin.scss';

.csat-actions {
  display: flex;
  gap: 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @include respond(phone) {
    gap: 8px;
  }
}
</style>
